import { z } from 'zod'

// Article Stream Status enum
export const ArticleStreamStatus = {
  Pending: 'pending',
  Streaming: 'streaming',
  Completed: 'completed',
  Error: 'error',
} as const

export type ArticleStreamStatusValue =
  (typeof ArticleStreamStatus)[keyof typeof ArticleStreamStatus]

// Article stream metadata schema
export const articleStreamMetadataSchema = z
  .object({
    category: z.string().optional(),
    tags: z.array(z.string()).optional(),
    requestedBy: z.string().optional(),
    prompt: z.string().optional(),
  })
  .optional()

// Article stream document schema (for Firestore)
export const articleStreamDocumentSchema = z.object({
  articleId: z.string(),
  title: z.string().default(''),
  content: z.string().default(''),
  status: z.enum(['pending', 'streaming', 'completed', 'error']).default('pending'),
  error: z.string().optional(),
  createdAt: z.any().nullable(), // Firestore Timestamp
  updatedAt: z.any().nullable(), // Firestore Timestamp
  completedAt: z.any().nullable().optional(), // Firestore Timestamp
  metadata: articleStreamMetadataSchema,
})

// Schema for creating/initializing an article stream
export const articleStreamCreateSchema = z.object({
  articleId: z.string(),
  title: z.string().optional(),
  metadata: articleStreamMetadataSchema,
})

// Schema for updating an article stream
export const articleStreamUpdateSchema = z.object({
  title: z.string().optional(),
  content: z.string().optional(),
  status: z.enum(['pending', 'streaming', 'completed', 'error']).optional(),
  error: z.string().optional(),
})

// Inferred types
export type ArticleStreamDocument = z.infer<typeof articleStreamDocumentSchema>
export type ArticleStreamCreate = z.infer<typeof articleStreamCreateSchema>
export type ArticleStreamUpdate = z.infer<typeof articleStreamUpdateSchema>
export type ArticleStreamMetadata = z.infer<typeof articleStreamMetadataSchema>

/**
 * Prepare data for Firestore by removing undefined values and applying defaults.
 * Firestore doesn't accept undefined values.
 */
export const prepareForFirestore = <T extends z.ZodTypeAny>(
  schema: T,
  data: unknown
): z.infer<T> => {
  // Parse with defaults applied
  const parsed = schema.parse(data)

  // Recursively remove undefined values
  const removeUndefined = (obj: any): any => {
    if (obj === null || obj === undefined) return obj
    if (Array.isArray(obj)) return obj.map(removeUndefined)
    if (typeof obj === 'object') {
      return Object.fromEntries(
        Object.entries(obj)
          .filter(([_, v]) => v !== undefined)
          .map(([k, v]) => [k, removeUndefined(v)])
      )
    }
    return obj
  }

  return removeUndefined(parsed)
}

/**
 * Create a Firestore-safe article stream document
 */
export const createArticleStreamData = (
  data: ArticleStreamCreate
): Omit<ArticleStreamDocument, 'createdAt' | 'updatedAt'> => {
  return prepareForFirestore(
    articleStreamDocumentSchema.omit({ createdAt: true, updatedAt: true }),
    {
      articleId: data.articleId,
      title: data.title || '',
      content: '',
      status: 'pending',
      metadata: data.metadata || {},
    }
  )
}
